import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  offset,
  shift,
  autoUpdate,
} from "@floating-ui/dom";

export default class extends Controller {
  static targets = ["button", "panel", "input", "option", "label", "hidden", "clear"];
  static values = {
    openClass: String,
    multiple: Boolean,
    prompt: String,
  };

  connect() {
    this.isOpen = false;
    this.highlightIndex = -1;
    this.cleanupFloating = null;
    if (this.multipleValue) this._syncMultipleLabel();
    this._updateClearIconVisibility();
  }

  // ----- open / close -----

  async open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.panelTarget.hidden = false;
    this.buttonTarget.setAttribute("aria-expanded", "true");
    this.element.classList.add(this.openClassValue || "");

    // focus search
    if (this.hasInputTarget) {
      requestAnimationFrame(() => this.inputTarget?.focus());
    }

    // floating-ui positioning
    this.cleanupFloating = autoUpdate(
      this.buttonTarget,
      this.panelTarget,
      () => {
        computePosition(this.buttonTarget, this.panelTarget, {
          placement: "bottom-start",
          middleware: [offset(4), shift({ padding: 8 })],
        }).then(({ x, y }) => {
          Object.assign(this.panelTarget.style, {
            top: `${y}px`,
          });
        });
      }
    );

    this._bindOutsideEvents();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    if (this.hasInputTarget) {
      this.inputTarget.value = "";
      this.filter();
    }

    this.panelTarget.hidden = true;
    this.buttonTarget.setAttribute("aria-expanded", "false");
    this.element.classList.remove(this.openClassValue || "");
    this.highlightIndex = -1;
    this._updateHighlighted();

    if (this.cleanupFloating) {
      this.cleanupFloating();
      this.cleanupFloating = null;
    }

    this._unbindOutsideEvents();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ----- outside click / esc -----

  _bindOutsideEvents() {
    this._outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) this.close();
    };
    this._escHandler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        this.buttonTarget.focus();
      }
    };
    document.addEventListener("click", this._outsideClickHandler);
    document.addEventListener("keydown", this._escHandler);
  }

  _unbindOutsideEvents() {
    document.removeEventListener("click", this._outsideClickHandler);
    document.removeEventListener("keydown", this._escHandler);
  }

  // ----- selection -----

  selectOption(event) {
    if (event.target.closest(".app-select-option-eye")) return;

    const option = event.currentTarget;
    const value = option.dataset.value;
    const label = option.dataset.label;
    const tag = option.dataset.tag;

    if (this.multipleValue) {
      this._toggleMultipleOption(option);
      return;
    }

    this._updateValue(value, { ...option.dataset });
    if (this.hasLabelTarget) {
      if (tag) {
        this.labelTarget.innerHTML = `${this._escapeHtml(label)} <span class="app-select-option-tag">${this._escapeHtml(tag)}</span>`;
      } else {
        this.labelTarget.textContent = label;
      }
    }
    this.optionTargets.forEach((el) =>
      el.classList.remove("app-select-option-selected")
    );
    option.classList.add("app-select-option-selected");

    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }

    this.filter();
    this.close();
    this.buttonTarget.focus();
    this._updateClearIconVisibility();
  }

  clear(event) {
    event.stopPropagation();

    if (this.multipleValue) {
      this.optionTargets.forEach((el) =>
        el.classList.remove("app-select-option-selected")
      );
      this._syncMultipleInputs();
      this._syncMultipleLabel();
      this._dispatchChange({ value: [], values: [] });
      this._updateClearIconVisibility();
      return;
    }

    this._updateValue("");
    if (this.hasLabelTarget) this.labelTarget.textContent = this.promptValue || "Select an option";
    this.optionTargets.forEach((el) =>
      el.classList.remove("app-select-option-selected")
    );
    this._updateClearIconVisibility();
  }

  _updateValue(value, optionData = {}) {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = value;
      this._dispatchChange({ ...optionData, value: this.hiddenTarget.value });
    }
  }

  _toggleMultipleOption(option) {
    option.classList.toggle("app-select-option-selected");
    this._syncMultipleInputs();
    this._syncMultipleLabel();
    this._dispatchChange({ ...option.dataset, value: this._selectedMultipleValues(), values: this._selectedMultipleValues() });
    this._updateClearIconVisibility();

    if (this.hasInputTarget) this.inputTarget.value = "";
    this.filter();
    option.focus();
  }

  _syncMultipleInputs() {
    if (!this.hasHiddenTarget) return;

    const selectedValues = this._selectedMultipleValues();

    if (this.hiddenTarget.tagName === "SELECT") {
      Array.from(this.hiddenTarget.options).forEach((option) => {
        const selected = selectedValues.includes(option.value);
        option.selected = selected;
        option.defaultSelected = selected;
      });
      return;
    }

    const name = this.hiddenTarget.name;
    this.hiddenTarget.value = "";
    this.hiddenTargets.slice(1).forEach((input) => input.remove());

    let anchor = this.hiddenTarget;
    selectedValues.forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.className = "select-hidden-input-value";
      input.name = name;
      input.value = value;
      input.dataset.appSelectTarget = "hidden";
      input.autocomplete = "custom_select_input";
      anchor.insertAdjacentElement("afterend", input);
      anchor = input;
    });
  }

  _syncMultipleLabel() {
    if (!this.hasLabelTarget) return;

    const labels = this.optionTargets
      .filter((option) => option.classList.contains("app-select-option-selected"))
      .map((option) => option.dataset.label);

    this.labelTarget.textContent = labels.length ? labels.join(", ") : (this.promptValue || "Select an option");
  }

  _selectedMultipleValues() {
    return this.optionTargets
      .filter((option) => option.classList.contains("app-select-option-selected"))
      .map((option) => option.dataset.value);
  }

  _dispatchChange(detail = {}) {
    if (!this.hasHiddenTarget) return;

    this.hiddenTarget.dispatchEvent(
      new CustomEvent('app-select:change', {
        bubbles: true,
        detail
      })
    );
  }

  _updateClearIconVisibility() {
    if (!this.hasClearTarget) return;
    const hasValue = this.multipleValue ? this._selectedMultipleValues().length > 0 : this.hasHiddenTarget && this.hiddenTarget.value;
    this.clearTarget.classList.toggle("d-none", !hasValue);
  }

  // ----- filtering -----

  filter() {
    let query = "";
    if (this.hasInputTarget) {
      query = this.inputTarget.value.trim().toLowerCase();
    }

    this.optionTargets.forEach((el) => {
      if (el.dataset.disabledByStage === 'true') {
        el.hidden = true;
        return;
      }
      const label = el.dataset.label.toLowerCase();
      const matches = label.includes(query);
      el.hidden = !matches;
    });

    this.highlightIndex = -1;
    this._updateHighlighted();
  }

  // ----- keyboard handling -----

  buttonKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;

    event.preventDefault();
    if (!this.isOpen) this.open();

    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      this._moveHighlight(event.key === "ArrowDown" ? 1 : -1);
    }
  }

  listKeydown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this._moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this._moveHighlight(-1);
        break;
      case "Enter":
        event.preventDefault();
        if (this.highlightIndex >= 0) {
          this.optionTargets[this.highlightIndex].click();
        }
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.buttonTarget.focus();
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  _visibleOptions() {
    return this.optionTargets.filter((el) => !el.hidden);
  }

  _moveHighlight(delta) {
    const visible = this._visibleOptions();
    if (!visible.length) return;

    // map current optionTargets index → visible index
    const currentVisibleIndex =
      this.highlightIndex >= 0
        ? visible.indexOf(this.optionTargets[this.highlightIndex])
        : -1;

    let nextVisibleIndex = currentVisibleIndex + delta;
    if (nextVisibleIndex < 0) nextVisibleIndex = visible.length - 1;
    if (nextVisibleIndex >= visible.length) nextVisibleIndex = 0;

    const nextOption = visible[nextVisibleIndex];
    this.highlightIndex = this.optionTargets.indexOf(nextOption);
    this._updateHighlighted();
    nextOption.focus();
  }

  _updateHighlighted() {
    this.optionTargets.forEach((el, index) => {
      if (index === this.highlightIndex) {
        el.classList.add("app-select-option-active");
      } else {
        el.classList.remove("app-select-option-active");
      }
    });
  }

  _escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
